import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ArrowRight, ArrowDown, Plus } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import IntersectGrid, { CornerTicks } from "@/components/IntersectMotif";
import { IMG, INTERSECTIONS, WHAT_WE_DO, PRINCIPLES, NETWORK_NODES } from "@/lib/content";
import { endpoints } from "@/lib/api";
import { useLang } from "@/lib/i18n";

// Subtle image backdrop for otherwise-black sections.
const Backdrop = ({ src, opacity = 0.32 }) => (
  <div className="absolute inset-0 overflow-hidden" aria-hidden>
    <img src={src} alt="" className="w-full h-full object-cover" style={{ opacity: Math.max(opacity, 0.3) }} />
    <div className="absolute inset-0 bg-gradient-to-b from-obsidian/85 via-obsidian/45 to-obsidian/90" />
  </div>
);

const Hero = ({ settings = {} }) => {
  const { t, isAr } = useLang();
  const headline = (isAr ? settings.hero_headline_ar : settings.hero_headline) || t("Where Capital, Opportunity & Partnerships Intersect.");
  const subtext = (isAr ? settings.hero_subtext_ar : settings.hero_subtext) || t("Intersect Holding builds ventures, creates strategic partnerships and connects capital with opportunities across Saudi Arabia and international markets.");
  return (
    <section className="relative h-screen min-h-[720px] flex flex-col justify-center overflow-hidden" data-testid="hero">
      <div className="absolute inset-0">
        <motion.img src={IMG.heroRiyadh} alt="Riyadh skyline" className="w-full h-full object-cover"
          initial={{ scale: 1.12 }} animate={{ scale: 1 }} transition={{ duration: 6, ease: "easeOut" }} />
        <div className="absolute inset-0 bg-obsidian/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/30 to-obsidian/60" />
      </div>
      <IntersectGrid cols={6} rows={4} className="opacity-70" />
      <div className="container-x relative z-10">
        <Reveal><p className="eyebrow mb-8">{t("Capital · Partnerships · Opportunity · Saudi Arabia")}</p></Reveal>
        <Reveal delay={0.15}>
          <h1 className="max-w-5xl text-[44px] leading-[1.05] sm:text-6xl lg:text-7xl xl:text-[92px] font-light tracking-tight gloss-heading">
            {headline}
          </h1>
        </Reveal>
        <Reveal delay={0.3}>
          <p className="mt-8 max-w-2xl text-lg md:text-xl leading-relaxed text-soft-stone">
            {subtext}
          </p>
        </Reveal>
        <Reveal delay={0.45}>
          <div className="mt-12 flex flex-wrap items-center gap-4">
            <Link to="/about" className="btn-solid" data-testid="hero-explore">{t("Explore Intersect")}</Link>
            <Link to="/contact" className="btn-gold" data-testid="hero-partner">{t("Partner With Us")}</Link>
          </div>
        </Reveal>
      </div>
      <motion.div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 text-muted-text"
        animate={{ y: [0, 8, 0] }} transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }} data-testid="scroll-indicator">
        <span className="text-[10px] tracking-[0.3em] uppercase">{t("Scroll")}</span>
        <ArrowDown size={16} strokeWidth={1.5} />
      </motion.div>
    </section>
  );
};

const Philosophy = () => {
  const { t } = useLang();
  const [active, setActive] = React.useState(0);
  return (
    <section className="section-pad relative overflow-hidden" data-testid="philosophy">
      <Backdrop src={IMG.kafdAerial} opacity={0.32} />
      <div className="container-x relative z-10">
        <Reveal><p className="eyebrow mb-8">{t("Intersect Philosophy")}</p></Reveal>
        <Reveal delay={0.1}>
          <h2 className="max-w-4xl text-3xl md:text-5xl font-light tracking-tight leading-tight text-ivory">
            {t("We operate at the intersection of capital, relationships and opportunity.")}
          </h2>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-px bg-primary-gold/10 border border-primary-gold/10">
          {INTERSECTIONS.map((it, i) => (
            <button key={it.key} onMouseEnter={() => setActive(i)} onFocus={() => setActive(i)} data-testid={`philosophy-${i}`}
              className={`lg:col-span-3 text-left card-lift bg-obsidian/80 p-8 md:p-10 min-h-[200px] md:min-h-[220px] flex flex-col justify-between transition-colors duration-500 ${active === i ? "bg-charcoal" : ""}`}>
              <span className="text-primary-gold text-xs tracking-[0.25em]">0{i + 1}</span>
              <div>
                <h3 className="text-xl md:text-2xl font-light tracking-tight text-ivory mb-4">{t(it.key)}</h3>
                <p className={`text-sm leading-relaxed text-muted-text transition-opacity duration-500 ${active === i ? "opacity-100" : "opacity-40"}`}>{t(it.text)}</p>
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

const AboutIntro = ({ settings = {} }) => {
  const { t, isAr } = useLang();
  const heading = (isAr ? settings.about_heading_ar : settings.about_heading) || t("More than capital.");
  const body = (isAr ? settings.about_body_ar : settings.about_body);
  return (
    <section className="section-pad relative bg-charcoal grain" data-testid="about-intro">
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        <div className="lg:col-span-7">
          <Reveal><p className="eyebrow mb-8">{t("Intersect Holding")}</p></Reveal>
          <Reveal delay={0.1}><h2 className="text-4xl md:text-6xl font-light tracking-tight text-ivory">{heading}</h2></Reveal>
          <Reveal delay={0.2}>
            <div className="mt-8 space-y-6 max-w-2xl text-lg leading-relaxed text-soft-stone">
              <p>{body || t("Intersect Holding is a Saudi investment and strategic holding company developing ventures, partnerships and long-term commercial relationships across Saudi Arabia and international markets.")}</p>
              {!body && <p>{t("We identify opportunities where capital, relationships, technology, market access and execution can create sustainable value.")}</p>}
            </div>
          </Reveal>
          <Reveal delay={0.3}>
            <Link to="/about" className="link-underline mt-10 text-sm tracking-wide uppercase" data-testid="about-intro-cta">
              {t("Discover Intersect")} <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
          </Reveal>
        </div>
        <div className="lg:col-span-5">
          <Reveal delay={0.2}>
            <div className="relative">
              <div className="absolute -inset-3 border border-primary-gold/15" />
              <img src={IMG.boardroomView} alt="" className="relative w-full aspect-[4/5] object-cover" />
              <CornerTicks />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const WhatWeDo = () => {
  const { t } = useLang();
  return (
    <section className="section-pad relative overflow-hidden" data-testid="what-we-do">
      <Backdrop src={IMG.skylineSunset} opacity={0.32} />
      <div className="container-x relative z-10">
        <Reveal><p className="eyebrow mb-8">{t("What We Do")}</p></Reveal>
        <div className="divide-y divide-white/5 border-t border-white/5">
          {WHAT_WE_DO.map((s) => (
            <Reveal key={s.no}>
              <Link to={s.to} className="group grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 py-12 md:py-16 items-start" data-testid={`service-${s.no}`}>
                <div className="md:col-span-2 flex items-center gap-4"><span className="text-primary-gold text-sm tracking-[0.25em]">{s.no}</span></div>
                <div className="md:col-span-4">
                  <p className="eyebrow mb-3 text-muted-gold">{t(s.label)}</p>
                  <h3 className="text-2xl md:text-3xl font-light tracking-tight text-ivory group-hover:text-primary-gold transition-colors duration-300">{t(s.headline)}</h3>
                </div>
                <div className="md:col-span-5 md:col-start-8">
                  <p className="text-base leading-relaxed text-muted-text">{t(s.body)}</p>
                  <span className="link-underline mt-6 text-sm tracking-wide uppercase text-primary-gold">{t(s.cta)} <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" /></span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const SaudiOpportunity = ({ stats }) => {
  const { t, isAr } = useLang();
  return (
    <section className="relative section-pad overflow-hidden" data-testid="saudi-opportunity">
      <div className="absolute inset-0">
        <img src={IMG.cityNight} alt="" className="w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-obsidian/80" />
      </div>
      <IntersectGrid cols={4} rows={2} />
      <div className="container-x relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <Reveal><p className="eyebrow mb-8">{t("Saudi Arabia")}</p></Reveal>
            <Reveal delay={0.1}><h2 className="text-3xl md:text-5xl font-light tracking-tight text-ivory">{t("A market being built for the next generation.")}</h2></Reveal>
            <Reveal delay={0.2}>
              <div className="mt-8 space-y-6 max-w-2xl text-lg leading-relaxed text-soft-stone">
                <p>{t("Saudi Arabia is undergoing one of the world's most ambitious economic transformations. New industries, investment platforms, technologies and commercial ecosystems are being created across the Kingdom.")}</p>
                <p>{t("Intersect identifies where these changes create opportunities for investment, partnerships and new ventures.")}</p>
              </div>
            </Reveal>
            <Reveal delay={0.3}>
              <Link to="/saudi-arabia" className="link-underline mt-10 text-sm tracking-wide uppercase text-primary-gold" data-testid="saudi-cta">
                {t("Explore Saudi Opportunities")} <ArrowRight size={16} strokeWidth={1.5} />
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-5 flex flex-col justify-center gap-px">
            {(stats || []).map((st, i) => (
              <Reveal key={st.id || i} delay={0.1 * i}>
                <div className="border-t border-primary-gold/15 py-8 flex items-baseline gap-6">
                  <span className="serif-italic text-5xl md:text-6xl text-luxury-gold">{st.value}</span>
                  <span className="text-sm tracking-wide uppercase text-muted-text">{isAr && st.label_ar ? st.label_ar : t(st.label)}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const AreasWeExplore = ({ sectors }) => {
  const { t } = useLang();
  return (
    <section className="section-pad relative overflow-hidden" data-testid="areas-we-explore">
      <Backdrop src={IMG.geometric} opacity={0.32} />
      <div className="container-x relative z-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <Reveal><p className="eyebrow mb-6">{t("Strategic Sectors")}</p></Reveal>
            <Reveal delay={0.1}><h2 className="text-3xl md:text-5xl font-light tracking-tight text-ivory">{t("Areas We Explore")}</h2></Reveal>
          </div>
          <Reveal delay={0.2}><p className="max-w-sm text-sm text-muted-text">{t("Sectors we actively study for investment, partnership and venture-building potential.")}</p></Reveal>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-primary-gold/10 border border-primary-gold/10">
          {(sectors || []).map((s, i) => (
            <div key={s.id || i} className="group relative card-lift bg-obsidian/80 p-7 md:p-8 min-h-[150px] md:min-h-[130px] flex items-end transition-colors duration-300 hover:bg-charcoal" data-testid={`sector-${i}`}>
              <span className="absolute top-4 right-4 text-[11px] text-primary-gold/50">{String(i + 1).padStart(2, "0")}</span>
              <span className="text-base md:text-base text-soft-stone group-hover:text-primary-gold transition-colors duration-300">{t(s.name)}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Partnerships = () => {
  const { t } = useLang();
  return (
    <section className="relative section-pad bg-charcoal overflow-hidden" data-testid="home-partnerships">
      <Backdrop src={IMG.boardroom} opacity={0.32} />
      <div className="container-x relative z-10">
        <Reveal><p className="eyebrow mb-8">{t("Strategic Partnerships")}</p></Reveal>
        <Reveal delay={0.1}><h2 className="max-w-4xl text-3xl md:text-5xl font-light tracking-tight text-ivory">{t("The right partnership can create an entirely new market.")}</h2></Reveal>
        <Reveal delay={0.2}><p className="mt-8 max-w-2xl text-lg leading-relaxed text-soft-stone">{t("We work across borders and sectors to identify organizations whose capabilities become more valuable when combined.")}</p></Reveal>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {["International Company", "Saudi Market Partner", "Investment Capital", "Local Market Access"].map((t2, i) => (
            <Reveal key={t2} delay={0.08 * i}>
              <div className="relative border border-primary-gold/15 p-8 min-h-[140px] flex items-center bg-obsidian/80">
                <p className="text-lg font-light text-ivory">{t(t2)}</p>
                <Plus className="absolute -right-[10px] top-1/2 -translate-y-1/2 text-primary-gold hidden lg:block bg-charcoal" size={18} strokeWidth={1.5} />
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-8 flex items-center gap-6">
            <ArrowDown className="text-primary-gold" size={20} strokeWidth={1.5} />
            <p className="serif-italic text-2xl md:text-3xl text-luxury-gold">{t("Joint Venture · Strategic Partnership · New Venture")}</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const InvestmentApproach = () => {
  const { t } = useLang();
  return (
    <section className="section-pad relative overflow-hidden" data-testid="investment-approach">
      <Backdrop src={IMG.abstractGold} opacity={0.32} />
      <div className="container-x relative z-10">
        <Reveal><p className="eyebrow mb-8">{t("Investment Approach")}</p></Reveal>
        <Reveal delay={0.1}><h2 className="text-3xl md:text-5xl font-light tracking-tight text-ivory mb-10">{t("How we think about investment.")}</h2></Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-primary-gold/10 border border-primary-gold/10">
          {PRINCIPLES.map((p) => (
            <Reveal key={p.no}>
              <div className="card-lift bg-obsidian/80 p-8 min-h-[220px] md:min-h-[240px] flex flex-col justify-between hover:bg-charcoal transition-colors duration-300">
                <span className="text-primary-gold text-sm tracking-[0.25em]">{p.no}</span>
                <div>
                  <h3 className="text-base tracking-wide uppercase text-ivory mb-3">{t(p.title)}</h3>
                  <p className="text-sm leading-relaxed text-muted-text">{t(p.text)}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const PortfolioPreview = ({ items }) => {
  const { t, isAr } = useLang();
  return (
    <section className="section-pad bg-charcoal relative overflow-hidden" data-testid="portfolio-preview">
      <Backdrop src={IMG.jeddahWater} opacity={0.1} />
      <div className="container-x relative z-10">
        <div className="flex items-end justify-between gap-6 mb-10">
          <div>
            <Reveal><p className="eyebrow mb-6">{t("Portfolio / Ventures")}</p></Reveal>
            <Reveal delay={0.1}><h2 className="text-3xl md:text-5xl font-light tracking-tight text-ivory">{t("Building at the intersection.")}</h2></Reveal>
          </div>
          <Reveal delay={0.2} className="hidden md:block">
            <Link to="/portfolio" className="link-underline text-sm tracking-wide uppercase text-primary-gold">{t("View Portfolio")} <ArrowRight size={16} strokeWidth={1.5} /></Link>
          </Reveal>
        </div>
        {items && items.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-primary-gold/10 border border-primary-gold/10">
            {items.slice(0, 6).map((c, i) => (
              <Link key={c.id} to={`/portfolio/${c.slug}`} className="card-lift bg-obsidian p-8 group hover:bg-charcoal transition-colors" data-testid={`portfolio-card-${i}`}>
                <div className="flex items-center justify-between mb-8">
                  <span className="text-xs tracking-widest uppercase text-primary-gold">{t(c.relationship_type)}</span>
                  <span className="text-xs text-muted-text">{t(c.status)}</span>
                </div>
                <h3 className="text-2xl font-light text-ivory group-hover:text-primary-gold transition-colors">{isAr && c.name_ar ? c.name_ar : c.name}</h3>
                <p className="mt-3 text-sm text-muted-text">{c.sector} · {c.location}</p>
                <p className="mt-4 text-sm leading-relaxed text-soft-stone line-clamp-3">{isAr && c.description_ar ? c.description_ar : c.description}</p>
              </Link>
            ))}
          </div>
        ) : (
          <Reveal>
            <div className="relative border border-primary-gold/15 p-12 md:p-20 text-center" data-testid="portfolio-empty">
              <IntersectGrid cols={3} rows={2} />
              <div className="relative z-10">
                <h3 className="text-2xl md:text-4xl font-light text-ivory">{t("Our portfolio is taking shape.")}</h3>
                <p className="mt-6 max-w-xl mx-auto text-base leading-relaxed text-muted-text">{t("Intersect is actively evaluating ventures, investment opportunities and strategic partnerships.")}</p>
                <Link to="/opportunity" className="btn-gold mt-10" data-testid="portfolio-empty-cta">{t("Present an Opportunity")}</Link>
              </div>
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
};

const NetworkSection = () => {
  const { t } = useLang();
  const R = 42;
  return (
    <section className="section-pad relative overflow-hidden" data-testid="network-section">
      <Backdrop src={IMG.kafdTower} opacity={0.32} />
      <div className="container-x relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <Reveal><p className="eyebrow mb-8">{t("Relationship Network")}</p></Reveal>
          <Reveal delay={0.1}><h2 className="text-3xl md:text-5xl font-light tracking-tight text-ivory">{t("Relationships are infrastructure.")}</h2></Reveal>
          <Reveal delay={0.2}>
            <div className="mt-8 space-y-6 max-w-lg text-lg leading-relaxed text-soft-stone">
              <p>{t("Capital alone rarely builds transformative companies.")}</p>
              <p>{t("Intersect develops relationships across investors, founders, companies, institutions and markets to help create opportunities that would otherwise remain disconnected.")}</p>
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <Reveal delay={0.2}>
            <div className="relative aspect-square max-w-[520px] mx-auto">
              <svg viewBox="0 0 100 100" className="w-full h-full">
                {NETWORK_NODES.map((_, i) => {
                  const a = (i / NETWORK_NODES.length) * Math.PI * 2 - Math.PI / 2;
                  const x = 50 + R * Math.cos(a), y = 50 + R * Math.sin(a);
                  return <motion.line key={i} x1="50" y1="50" x2={x} y2={y} stroke="#C6A15B" strokeWidth="0.2" strokeOpacity="0.4"
                    initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }} transition={{ duration: 1, delay: i * 0.1 }} />;
                })}
                {NETWORK_NODES.map((_, i) => {
                  const a = (i / NETWORK_NODES.length) * Math.PI * 2 - Math.PI / 2;
                  const x = 50 + R * Math.cos(a), y = 50 + R * Math.sin(a);
                  return <circle key={i} cx={x} cy={y} r="1.1" fill="#C6A15B" />;
                })}
                <circle cx="50" cy="50" r="2" fill="#D4B66A" />
              </svg>
              {NETWORK_NODES.map((n, i) => {
                const a = (i / NETWORK_NODES.length) * Math.PI * 2 - Math.PI / 2;
                const x = 50 + (R + 6) * Math.cos(a), y = 50 + (R + 6) * Math.sin(a);
                return <span key={n} className="absolute text-[10px] md:text-xs tracking-wide uppercase text-muted-text -translate-x-1/2 -translate-y-1/2 whitespace-nowrap" style={{ left: `${x}%`, top: `${y}%` }}>{t(n)}</span>;
              })}
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-sm tracking-[0.25em] text-luxury-gold">{t("INTERSECT")}</span>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const InsightsPreview = ({ items }) => {
  const { t, isAr } = useLang();
  return (
    <section className="section-pad bg-charcoal relative overflow-hidden" data-testid="insights-preview">
      <Backdrop src={IMG.faisaliah} opacity={0.1} />
      <div className="container-x relative z-10">
        <div className="flex items-end justify-between gap-6 mb-10">
          <div>
            <Reveal><p className="eyebrow mb-6">{t("Perspective")}</p></Reveal>
            <Reveal delay={0.1}><h2 className="text-3xl md:text-5xl font-light tracking-tight text-ivory">{t("Insights")}</h2></Reveal>
          </div>
          <Reveal delay={0.2} className="hidden md:block">
            <Link to="/insights" className="link-underline text-sm tracking-wide uppercase text-primary-gold">{t("Read Perspective")} <ArrowRight size={16} strokeWidth={1.5} /></Link>
          </Reveal>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {(items || []).map((a, i) => (
            <Reveal key={a.id} delay={0.08 * i}>
              <Link to={`/insights/${a.slug}`} className="group block card-lift" data-testid={`insight-card-${i}`}>
                <div className="relative overflow-hidden aspect-[4/3] mb-6">
                  <img src={a.hero_image} alt={a.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-obsidian/20" />
                </div>
                <div className="flex items-center gap-3 text-xs tracking-wide uppercase text-primary-gold mb-3">
                  <span>{t(a.category)}</span><span className="text-white/20">·</span><span className="text-muted-text">{a.reading_time}</span>
                </div>
                <h3 className="text-xl font-light text-ivory group-hover:text-primary-gold transition-colors">{isAr && a.title_ar ? a.title_ar : a.title}</h3>
                <p className="mt-2 text-xs text-muted-text">{a.date}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export const FinalCTA = () => {
  const { t, isAr } = useLang();
  return (
    <section className="relative py-20 md:py-28 overflow-hidden" data-testid="final-cta">
      <div className="absolute inset-0">
        <img src={IMG.kafdAerial} alt="" className="w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-obsidian/85" />
      </div>
      <IntersectGrid cols={5} rows={3} />
      <div className="container-x relative z-10 text-center">
        <Reveal>
          <h2 className="max-w-4xl mx-auto text-3xl md:text-5xl lg:text-6xl font-light tracking-tight text-ivory">
            {isAr ? t("Great opportunities often begin with a conversation.") : (<>Great opportunities often begin with a <span className="serif-italic text-luxury-gold">conversation.</span></>)}
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-8 max-w-2xl mx-auto text-lg leading-relaxed text-soft-stone">
            {t("Whether you are building a company, exploring Saudi Arabia, seeking investment or looking for the right strategic partner, we would like to hear from you.")}
          </p>
        </Reveal>
        <Reveal delay={0.3}>
          <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
            <Link to="/contact" className="btn-solid" data-testid="finalcta-conversation">{t("Start a Conversation")}</Link>
            <Link to="/opportunity" className="btn-gold" data-testid="finalcta-opportunity">{t("Present an Opportunity")}</Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default function Home() {
  const { data } = useQuery({
    queryKey: ["home"],
    queryFn: async () => {
      const [settings, statistics, sectors, portfolio, insights] = await endpoints.home();
      return { settings: settings.data, statistics: statistics.data, sectors: sectors.data, portfolio: portfolio.data, insights: insights.data };
    },
  });

  return (
    <>
      <Hero settings={data?.settings} />
      <Philosophy />
      <AboutIntro settings={data?.settings} />
      <WhatWeDo />
      <SaudiOpportunity stats={data?.statistics} />
      <AreasWeExplore sectors={data?.sectors} />      <Partnerships />
      <InvestmentApproach />
      <PortfolioPreview items={data?.portfolio} />
      <NetworkSection />
      <InsightsPreview items={data?.insights} />
      <FinalCTA />
    </>
  );
}
